/**
 * Cross-subdomain redirect router.
 *
 * In production we deploy 4 separate frontends under afyanex.com:
 *   - www.afyanex.com    — marketing landing (anonymous)
 *   - app.afyanex.com    — caregiver + family (default authenticated home)
 *   - agency.afyanex.com — owner / admin console
 *   - admin.afyanex.com  — superadmin console
 *
 * After login (or page reload), we send the user to the correct portal.
 * The auth cookie is scoped to `.afyanex.com` so it travels with them.
 *
 * In preview / local dev (where REACT_APP_*_URL env vars are unset) we
 * fall back to in-app routes — i.e. behaves exactly like before.
 */

const APP_URL      = process.env.REACT_APP_APP_URL      || "";
const AGENCY_URL   = process.env.REACT_APP_AGENCY_URL   || "";
const ADMIN_URL    = process.env.REACT_APP_ADMIN_URL    || "";
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || "";

/** Build the post-login URL for a user. Always returns an absolute or
 *  relative path; never null. Mandatory-2FA users get redirected to the
 *  enrolment screen regardless of role. */
export function postLoginUrl(user) {
  if (!user) return "/login";
  if (user.requires_2fa_enrollment) return "/my-profile?enroll_2fa=1";

  const role = (user.role || "").toLowerCase();
  if (user.is_superadmin) {
    return ADMIN_URL ? `${ADMIN_URL}/superadmin` : "/superadmin";
  }
  if (role === "owner" || role === "admin") {
    return AGENCY_URL ? `${AGENCY_URL}/admin` : "/admin";
  }
  if (role === "caregiver") {
    return APP_URL ? `${APP_URL}/clients` : "/clients";
  }
  if (role === "family") {
    return APP_URL ? `${APP_URL}/family/portal` : "/family/portal";
  }
  return APP_URL ? `${APP_URL}/` : "/";
}

/** Navigate to the post-login URL — uses location.href when it's an
 *  absolute URL (cross-subdomain hop) and history pushState otherwise. */
export function redirectAfterLogin(user, navigate) {
  const url = postLoginUrl(user);
  if (url.startsWith("http://") || url.startsWith("https://")) {
    window.location.href = url;
  } else if (typeof navigate === "function") {
    navigate(url, { replace: true });
  } else {
    window.location.href = url;
  }
}

/** Marketing URL — used by /login's "back to home" link and the agency
 *  signup wizard's "already have an account? sign in" link. */
export function marketingUrl() {
  return MARKETING_URL || "/";
}
